import React, { useEffect, useMemo, useState } from "react";
import {
  FaMoneyBillWave,
  FaPen,
  FaPlusCircle,
  FaReceipt,
  FaSearch,
  FaUniversity,
  FaUserGraduate,
} from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";
import { getCachedOrFetch, getTenantCacheKey, invalidateCache } from "../utils/requestCache";
import AdminModalShell from "./AdminModalShell";
import { useAppTheme } from "../context/AppThemeContext";
import AdminResponsiveTabs from "./AdminResponsiveTabs";
import AdminMobileViewToggle from "./AdminMobileViewToggle";
import ModuleImportButton from "./ModuleImportButton";
import ModuleRefreshButton from "./ModuleRefreshButton";

const emptyForm = { id: "", studentId: "", studentName: "", studentQuery: "", className: "", feeType: "Monthly Fee", amount: "", paidAmount: "", dueDate: "", status: "PENDING", paymentMode: "UPI", lastPaidOn: "" };

const normalizeFee = (record = {}, index = 0) => ({
  id: record.id || record.feeId || record._id || `fee-${index + 1}`,
  studentId: record.studentId || record.student?.id || "",
  studentName: record.studentName || record.name || "Student",
  className: record.className || record.standard || "Class",
  feeType: record.feeType || record.type || "Monthly Fee",
  amount: Number(record.amount ?? record.totalAmount ?? 0),
  paidAmount: Number(record.paidAmount ?? record.paid ?? 0),
  dueDate: record.dueDate || record.nextDueDate || "",
  status: String(record.status || "PENDING").toUpperCase(),
  paymentMode: record.paymentMode || "Pending",
  lastPaidOn: record.lastPaidOn || record.paymentDate || "Not paid",
});

const normalizeStudentOption = (record = {}, index = 0) => {
  const profile = record.profile || record.studentProfile || record.studentDetails || {};
  const source = { ...record, ...profile };
  const firstName = source.firstName || source.first_name || source.givenName || "";
  const lastName = source.lastName || source.last_name || source.surname || "";
  const name = source.name || source.fullName || source.studentName || [firstName, lastName].filter(Boolean).join(" ") || "Unnamed Student";
  const className = source.className || source.class_name || source.studentClass || source.standard || "Class not assigned";
  const section = source.section || "";
  const rollNo = record.rollNo || record.rollNumber || "";
  const label = `${name} — ${className}${section ? ` ${section}` : ""}${rollNo ? ` — Roll ${rollNo}` : ""}`;
  return { id: record.id || record.studentId || record._id || `student-${index + 1}`, name, className, section, rollNo, label };
};

const extractStudents = (responseData) => {
  const payload = responseData?.data ?? responseData ?? [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.students)) return payload.students;
  if (Array.isArray(payload?.content)) return payload.content;
  return [];
};

const extractFees = (responseData) => {
  const payload = responseData?.data ?? responseData ?? [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.fees)) return payload.fees;
  if (Array.isArray(payload?.content)) return payload.content;
  return [];
};

const formatCurrency = (value) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(value || 0));

const feeTone = (status, isDarkMode) => {
  if (status === "PAID") return isDarkMode ? "border-emerald-900/70 bg-emerald-950/40 text-emerald-200" : "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (status === "PARTIAL") return isDarkMode ? "border-amber-900/70 bg-amber-950/40 text-amber-200" : "border-amber-200 bg-amber-50 text-amber-700";
  return isDarkMode ? "border-rose-900/70 bg-rose-950/40 text-rose-200" : "border-rose-200 bg-rose-50 text-rose-700";
};

const AdminFeeManagement = () => {
  const { isDarkMode } = useAppTheme();
  const [fees, setFees] = useState([]);
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFeeId, setSelectedFeeId] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [activeView, setActiveView] = useState("invoices");
  const [mobileRecordView, setMobileRecordView] = useState("cards");
  const [form, setForm] = useState(emptyForm);
  const [refreshVersion, setRefreshVersion] = useState(0);

  useEffect(() => {
    const fetchFees = async () => {
      setLoading(true);
      setStudentsLoading(true);
      try {
        const [feeResponse, studentResponse] = await Promise.all([
          getCachedOrFetch(getTenantCacheKey("admin:fees"), () => api.get(API_PATHS.admin.fees()), { forceRefresh: refreshVersion > 0 }),
          getCachedOrFetch(getTenantCacheKey("admin:students"), () => api.get(API_PATHS.admin.students()), { forceRefresh: refreshVersion > 0 }),
        ]);
        const nextStudents = extractStudents(studentResponse.data).map(normalizeStudentOption);
        const studentsById = new Map(nextStudents.map((student) => [String(student.id), student]));
        const nextFees = extractFees(feeResponse.data).map((record, index) => {
          const fee = normalizeFee(record, index);
          const student = studentsById.get(String(fee.studentId));
          return student ? { ...fee, studentName: student.name, className: `${student.className}${student.section ? ` - ${student.section}` : ""}`, studentQuery: student.label } : fee;
        });
        setStudents(nextStudents);
        setFees(nextFees);
        setSelectedFeeId(nextFees[0]?.id || "");
      } catch {
        setFees([]);
        setSelectedFeeId("");
        toast.error("Unable to load fee records.");
      } finally {
        setLoading(false);
        setStudentsLoading(false);
      }
    };
    fetchFees();
  }, [refreshVersion]);

  const selectedFee = useMemo(() => fees.find((fee) => fee.id === selectedFeeId) || null, [selectedFeeId, fees]);
  useEffect(() => { setForm(selectedFee || emptyForm); }, [selectedFee]);

  const filteredFees = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return fees.filter((fee) => !query || fee.studentName.toLowerCase().includes(query) || fee.className.toLowerCase().includes(query) || fee.feeType.toLowerCase().includes(query) || fee.status.toLowerCase().includes(query));
  }, [searchTerm, fees]);

  const summary = useMemo(() => ({
    billed: fees.reduce((sum, fee) => sum + fee.amount, 0),
    collected: fees.reduce((sum, fee) => sum + fee.paidAmount, 0),
    pending: fees.filter((fee) => fee.status !== "PAID").length,
    receipts: fees.filter((fee) => fee.paidAmount > 0).length,
  }), [fees]);

  const panelClass = isDarkMode ? "border-slate-800 bg-slate-900 shadow-black/20" : "border-slate-200 bg-white shadow-slate-200/60";

  const resetForm = () => { setSelectedFeeId(""); setForm({ ...emptyForm }); };
  const openCreateForm = () => { resetForm(); setIsFormOpen(true); };
  const closeForm = () => { resetForm(); setIsFormOpen(false); };
  const openEditForm = (feeId) => { setSelectedFeeId(feeId); setIsFormOpen(true); };
  const updateForm = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const selectStudent = (value) => {
    const student = students.find((item) => item.label === value);
    setForm((current) => ({
      ...current,
      studentQuery: value,
      studentId: student?.id || "",
      studentName: student?.name || "",
      className: student ? `${student.className}${student.section ? ` - ${student.section}` : ""}` : "",
    }));
  };

  const upsertFee = (nextFee) => {
    setFees((current) => {
      const exists = current.some((fee) => fee.id === nextFee.id);
      if (!exists) return [nextFee, ...current];
      return current.map((fee) => (fee.id === nextFee.id ? nextFee : fee));
    });
    setSelectedFeeId(nextFee.id);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (Number(form.amount) <= 0) {
      toast.error("Fee amount must be greater than 0.");
      return;
    }
    if (!form.id && !form.studentId) {
      toast.error("Select a student from the student list.");
      return;
    }
    setSaving(true);
    const isEditing = Boolean(form.id);
    const payload = isEditing
      ? { status: form.status }
      : { studentId: form.studentId, amount: Number(form.amount || 0), dueDate: form.dueDate, type: form.feeType };
    try {
      const response = isEditing ? await api.put(API_PATHS.admin.feeById(form.id), payload) : await api.post(API_PATHS.admin.fees(), payload);
      const nextFee = normalizeFee({ ...(response.data?.data ?? response.data ?? {}), ...payload, studentName: form.studentName, className: form.className, studentQuery: form.studentQuery, id: isEditing ? form.id : undefined }, fees.length);
      upsertFee(nextFee);
      invalidateCache(getTenantCacheKey("admin:fees"));
      resetForm();
      setIsFormOpen(false);
      toast.success(isEditing ? "Fee record updated." : "Fee record created.");
    } catch {
      resetForm();
      setIsFormOpen(false);
      toast.error("Failed to save fee record. Please try again.");
    } finally { setSaving(false); }
  };

  return (
    <div>
      <AdminResponsiveTabs tabs={[{ key: "metrics", label: "Metrics" }, { key: "invoices", label: "Invoices" }]} activeKey={activeView} onChange={setActiveView} tone="amber" isDarkMode={isDarkMode} />

      <div className={`border-x border-b p-6 shadow-lg ${panelClass}`}>
        {activeView === "metrics" && (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {[
                { label: "Billed", value: formatCurrency(summary.billed) },
                { label: "Collected", value: formatCurrency(summary.collected) },
                { label: "Pending records", value: summary.pending },
                { label: "Receipts", value: summary.receipts },
              ].map((item) => (
                <div key={item.label} className={`rounded-2xl border p-4 ${isDarkMode ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"}`}>
                  <p className={`text-xs font-semibold uppercase tracking-[0.16em] ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{item.label}</p>
                  <p className="mt-2 text-2xl font-bold">{item.value}</p>
                </div>
              ))}
            </div>
            <div className={`overflow-hidden rounded-2xl border ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className={isDarkMode ? "bg-slate-900 text-slate-300" : "bg-slate-100 text-slate-600"}>
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Metric</th>
                      <th className="px-4 py-3 text-left font-semibold">Value</th>
                    </tr>
                  </thead>
                  <tbody className={isDarkMode ? "bg-slate-950 text-slate-200" : "bg-white text-slate-800"}>
                    <tr className={`border-t ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}><td className="px-4 py-3">Total billed</td><td className="px-4 py-3">{formatCurrency(summary.billed)}</td></tr>
                    <tr className={`border-t ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}><td className="px-4 py-3">Total collected</td><td className="px-4 py-3">{formatCurrency(summary.collected)}</td></tr>
                    <tr className={`border-t ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}><td className="px-4 py-3">Pending records</td><td className="px-4 py-3">{summary.pending}</td></tr>
                    <tr className={`border-t ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}><td className="px-4 py-3">Receipts issued</td><td className="px-4 py-3">{summary.receipts}</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeView === "invoices" && (
          <>
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="relative flex-1">
                <FaSearch className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  className={`w-full rounded-2xl border py-2.5 pl-10 pr-3 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100 placeholder:text-slate-500" : "border-slate-300 bg-white text-slate-900"}`}
                  placeholder="Search student, class, fee type, status"
                />
              </div>
                <ModuleRefreshButton onRefresh={() => setRefreshVersion((value) => value + 1)} />
                <ModuleImportButton moduleKey="fees" label="Import fees" />
                <button
                type="button"
                onClick={openCreateForm}
                className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-amber-600 whitespace-nowrap"
              >
                <FaPlusCircle /> Create fee record
              </button>
            </div>

            <div className="mb-3">
              <AdminMobileViewToggle value={mobileRecordView} onChange={setMobileRecordView} isDarkMode={isDarkMode} />
            </div>

            {mobileRecordView === "cards" && (
              <div className="space-y-3 md:hidden">
                {loading ? (
                  <p className={`rounded-2xl p-6 text-center text-sm ${isDarkMode ? "bg-slate-950 text-slate-400" : "bg-slate-50 text-slate-500"}`}>Loading fee records...</p>
                ) : filteredFees.length === 0 ? (
                  <p className={`rounded-2xl p-6 text-center text-sm ${isDarkMode ? "bg-slate-950 text-slate-400" : "bg-slate-50 text-slate-500"}`}>No fee records found.</p>
                ) : filteredFees.map((fee) => (
                  <article key={fee.id} className={`rounded-2xl border p-4 ${isDarkMode ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-white"}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div><h3 className="font-bold">{fee.studentName}</h3><p className={`mt-0.5 text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{fee.className} · {fee.feeType}</p></div>
                      <span className={`rounded-full border px-2 py-1 text-[10px] font-semibold ${feeTone(fee.status, isDarkMode)}`}>{fee.status}</span>
                    </div>
                    <div className={`mt-3 grid grid-cols-2 gap-2 rounded-xl p-3 text-xs ${isDarkMode ? "bg-slate-900" : "bg-slate-50"}`}>
                      <div><p className="text-slate-400">Amount</p><p className="mt-1 font-semibold">{formatCurrency(fee.amount)}</p></div>
                      <div><p className="text-slate-400">Paid</p><p className="mt-1 font-semibold">{formatCurrency(fee.paidAmount)}</p></div>
                      <div className="col-span-2"><p className="text-slate-400">Due date</p><p className="mt-1 font-semibold">{fee.dueDate || "Not set"}</p></div>
                    </div>
                    <button type="button" onClick={() => openEditForm(fee.id)} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-amber-300 px-3 py-2 text-xs font-bold text-amber-700"><FaPen /> Update fee</button>
                  </article>
                ))}
              </div>
            )}

            <div className={`${mobileRecordView === "cards" ? "hidden md:block" : "block"} overflow-hidden rounded-2xl border ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className={isDarkMode ? "bg-slate-900 text-slate-300" : "bg-slate-100 text-slate-600"}>
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Student</th>
                      <th className="px-4 py-3 text-left font-semibold">Class</th>
                      <th className="px-4 py-3 text-left font-semibold">Fee type</th>
                      <th className="px-4 py-3 text-left font-semibold">Amount</th>
                      <th className="px-4 py-3 text-left font-semibold">Paid</th>
                      <th className="px-4 py-3 text-left font-semibold">Due date</th>
                      <th className="px-4 py-3 text-left font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody className={isDarkMode ? "bg-slate-950 text-slate-200" : "bg-white text-slate-800"}>
                    {loading ? (
                      <tr><td colSpan="7" className={`px-4 py-10 text-center ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Loading fee records...</td></tr>
                    ) : filteredFees.length === 0 ? (
                      <tr><td colSpan="7" className={`px-4 py-10 text-center ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>No fee records found.</td></tr>
                    ) : filteredFees.map((fee) => (
                      <tr
                        key={fee.id}
                        onClick={() => openEditForm(fee.id)}
                        className={`cursor-pointer border-t transition ${isDarkMode ? "border-slate-800 hover:bg-slate-800/60" : "border-slate-200 hover:bg-slate-50"} ${fee.id === selectedFeeId ? isDarkMode ? "bg-amber-950/20" : "bg-amber-50" : ""}`}
                      >
                        <td className="px-4 py-3 font-semibold">{fee.studentName}</td>
                        <td className="px-4 py-3">{fee.className}</td>
                        <td className="px-4 py-3">{fee.feeType}</td>
                        <td className="px-4 py-3">{formatCurrency(fee.amount)}</td>
                        <td className="px-4 py-3">{formatCurrency(fee.paidAmount)}</td>
                        <td className="px-4 py-3">{fee.dueDate}</td>
                        <td className="px-4 py-3">
                          <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${feeTone(fee.status, isDarkMode)}`}>
                            {fee.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      <AdminModalShell isOpen={isFormOpen} onClose={closeForm} isDarkMode={isDarkMode}>
        <div className={`rounded-3xl border p-6 shadow-lg ${panelClass}`}>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Fee form</p>
              <h2 className="mt-2 text-2xl font-bold">{form.id ? "Update fee record" : "Create fee record"}</h2>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Student name</label>
                <input type="text" list="fee-student-options" value={form.studentQuery || form.studentName} onChange={(event) => selectStudent(event.target.value)} disabled={Boolean(form.id) || studentsLoading} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-100 disabled:cursor-not-allowed disabled:opacity-70 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} placeholder={studentsLoading ? "Loading students…" : "Search student by name, class, or roll number"} required />
                <datalist id="fee-student-options">{students.map((student) => <option key={student.id} value={student.label} />)}</datalist>
                {!studentsLoading && students.length === 0 && <p className="mt-1 text-xs text-amber-600">No students are available. Create a student before adding a fee record.</p>}
              </div>
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Class</label>
                <input type="text" value={form.className} readOnly aria-readonly="true" className={`w-full cursor-not-allowed rounded-2xl border px-3 py-3 text-sm ${isDarkMode ? "border-slate-700 bg-slate-900 text-slate-300" : "border-slate-300 bg-slate-100 text-slate-600"}`} placeholder="Populated from selected student" required />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Fee type</label>
                <select value={form.feeType} onChange={(event) => updateForm("feeType", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`}>
                  <option value="Monthly Fee">Monthly Fee</option>
                  <option value="Admission Fee">Admission Fee</option>
                  <option value="Exam Fee">Exam Fee</option>
                </select>
              </div>
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Due date</label>
                <input type="date" value={form.dueDate} onChange={(event) => updateForm("dueDate", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} required />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Amount</label>
                <input type="number" min="0" value={form.amount} onChange={(event) => updateForm("amount", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} placeholder="3500" required />
              </div>
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Paid amount</label>
                <input type="number" min="0" value={form.paidAmount} onChange={(event) => updateForm("paidAmount", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} placeholder="0" required />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Status</label>
                <select value={form.status} onChange={(event) => updateForm("status", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`}>
                  <option value="PAID">Paid</option>
                  <option value="PARTIAL">Partial</option>
                  <option value="PENDING">Pending</option>
                </select>
              </div>
              <div>
                <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Payment mode</label>
                <select value={form.paymentMode} onChange={(event) => updateForm("paymentMode", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`}>
                  <option value="UPI">UPI</option>
                  <option value="Cash">Cash</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>
            </div>
            <div>
              <label className={`mb-1 block text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>Last paid on</label>
              <input type="text" value={form.lastPaidOn} onChange={(event) => updateForm("lastPaidOn", event.target.value)} className={`w-full rounded-2xl border px-3 py-3 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-100 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300"}`} placeholder="2026-04-08 or Not paid" />
            </div>
            <div className="flex flex-wrap gap-3">
              <button type="submit" disabled={saving} className="inline-flex items-center rounded-full bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-amber-600 disabled:opacity-70">{saving ? "Saving..." : form.id ? "Update fee record" : "Create fee record"}</button>
              <button type="button" onClick={() => setIsFormOpen(false)} className={`inline-flex items-center rounded-full border px-5 py-2.5 text-sm font-semibold transition ${isDarkMode ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>Cancel</button>
            </div>
          </form>
        </div>
      </AdminModalShell>
    </div>
  );
};

export default AdminFeeManagement;
